import React, { useState, useEffect } from 'react';
import { tierDisplayName } from '../utils/tierNames';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import CreditDashboard from '../components/CreditDashboard';
import NotificationBell from '../components/NotificationBell';
import AppShell from '../components/AppShell';
import { 
  Check, ArrowRight, Loader2,
  Calendar, Coins, Crown, Sparkles, CreditCard,
  ExternalLink, Shield, Zap,
  Headphones, Download, CheckCircle2, XCircle, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Plan Card Component
const PlanCard = ({ 
  name, 
  price, 
  monthlyEquivalent,
  savings,
  features, 
  credits, 
  isCurrentPlan, 
  isFounder, 
  isPopular,
  billingCycle,
  onUpgrade,
  loading 
}) => {
  const isActivePlan = isCurrentPlan || (isFounder && name === 'Elite');
  const isCreatorPlus = name === 'Creator+';
  return (
    <div 
      className={`relative bg-zinc-900/50 border rounded-2xl p-6 transition-all ${
        isActivePlan && isFounder
          ? 'border-amber-500/50 ring-2 ring-amber-500/20 shadow-[0_0_24px_-4px_rgba(245,158,11,0.15)]'
          : isActivePlan
            ? 'border-emerald-500/50 ring-2 ring-emerald-500/20 shadow-[0_0_24px_-4px_rgba(16,185,129,0.15)]'
            : isPopular 
              ? 'border-orange-500/50 ring-2 ring-orange-500/20 shadow-[0_0_24px_-4px_rgba(249,115,22,0.15)]' 
              : 'border-zinc-800/50 hover:border-zinc-700'
      }`}
      data-testid={`plan-card-${name.toLowerCase().replace('+', '-plus')}`}
    >
      {isPopular && !isActivePlan && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-orange-500 text-xs font-medium text-white whitespace-nowrap" data-testid="best-value-badge">
          Best Value for Creators
        </div>
      )}

      {isActivePlan && isFounder && (
        <div className="absolute -top-3 right-4 px-3 py-1 rounded-full bg-amber-500 text-xs font-medium text-white flex items-center gap-1" data-testid="founder-plan-badge">
          <Crown className="w-3 h-3" />
          Unlimited Access
        </div>
      )}

      {isCurrentPlan && !isFounder && (
        <div className="absolute -top-3 right-4 px-3 py-1 rounded-full bg-emerald-500 text-xs font-medium text-white flex items-center gap-1" data-testid="current-plan-badge">
          <CheckCircle2 className="w-3 h-3" />
          Current Plan
        </div>
      )}

      <h3 className="text-xl font-bold text-white mb-1">{name}</h3>
      {isCreatorPlus && (
        <p className="text-[11px] text-orange-400/80 mb-2">Built for creators who publish daily and scale content production.</p>
      )}
      
      {/* Price */}
      <div className="mb-4">
        <div className="flex items-baseline gap-1">
          <span className="text-3xl font-bold text-white">
            ${billingCycle === 'annual' ? monthlyEquivalent?.toFixed(0) : price}
          </span>
          <span className="text-zinc-500">/month</span>
        </div>
        {billingCycle === 'annual' && savings > 0 && (
          <div className="mt-1 space-y-1">
            <p className="text-sm text-zinc-500">Billed ${price}/year</p>
            <p className="text-sm text-emerald-400">Save ${savings}/year</p>
          </div>
        )}
      </div>

      {/* Credits */}
      {credits > 0 && (
        <div className="flex items-center gap-2 mb-4 p-2 bg-primary/10 rounded-lg">
          <Coins className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium text-primary">
            {credits.toLocaleString()} credits/month
          </span>
        </div>
      )}

      {/* Features */}
      <ul className="space-y-2 mb-6">
        {features.map((feature, idx) => (
          <li key={idx} className="flex items-center gap-2 text-sm text-zinc-400">
            <Check className="w-4 h-4 text-primary flex-shrink-0" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      {/* Action Button */}
      {isFounder ? (
        <Button disabled className="w-full bg-zinc-800/50 text-zinc-500 border-zinc-700/50" variant="outline">
          {isActivePlan ? 'Your Plan' : 'Included'}
        </Button>
      ) : isCurrentPlan ? (
        <Button disabled className="w-full bg-emerald-500/20 text-emerald-400 border-emerald-500/30" variant="outline">
          Current Plan
        </Button>
      ) : (
        <Button
          className={`w-full ${isPopular ? 'btn-oracle' : 'border-zinc-700 hover:bg-zinc-800'}`}
          variant={isPopular ? 'default' : 'outline'}
          onClick={onUpgrade}
          disabled={loading}
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              Upgrade <ArrowRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
      )}
    </div>
  );
};

// Feature Comparison Row (No Starter column)
const FeatureRow = ({ feature, standard, pro, creatorPlus, elite, agencyScale }) => {
  const renderValue = (val) => {
    if (val === true) return <Check className="w-4 h-4 text-emerald-400 mx-auto" />;
    if (val === false) return <XCircle className="w-4 h-4 text-zinc-600 mx-auto" />;
    return <span className="text-sm text-zinc-400">{val}</span>;
  };

  return (
    <tr className="border-b border-zinc-800/50">
      <td className="py-3 px-4 text-sm text-zinc-300">{feature}</td>
      <td className="py-3 px-4 text-center">{renderValue(standard)}</td>
      <td className="py-3 px-4 text-center">{renderValue(pro)}</td>
      <td className="py-3 px-4 text-center bg-orange-500/5">{renderValue(creatorPlus)}</td>
      <td className="py-3 px-4 text-center">{renderValue(elite)}</td>
      <td className="py-3 px-4 text-center">{renderValue(agencyScale)}</td>
    </tr>
  );
};

const BillingPage = () => {
  const { user, token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [tiers, setTiers] = useState({});
  const [annualPricing, setAnnualPricing] = useState({});
  const [tierCredits, setTierCredits] = useState({});
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [billingCycle, setBillingCycle] = useState(user?.billing_cycle || 'monthly');
  const [creditData, setCreditData] = useState(null);

  useEffect(() => {
    fetchPricingData();
    checkPaymentStatus();
    // Fetch credit balance for billing page banner
    if (token) {
      axios.get(`${API}/credits/balance`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setCreditData(r.data))
        .catch(() => {});
    }
  }, []);

  const fetchPricingData = async () => {
    try {
      const response = await axios.get(`${API}/credits/pricing`);
      setTiers(response.data.tiers || {});
      setAnnualPricing(response.data.annual_pricing || {});
      setTierCredits(response.data.tier_credits || {});
    } catch (error) {
      try {
        const tierResponse = await axios.get(`${API}/subscriptions/tiers`);
        setTiers(tierResponse.data);
      } catch (e) {
        toast.error('Failed to load pricing');
      }
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async () => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    const status = params.get('payment');
    const creditsPurchased = params.get('credits_purchased');

    if (creditsPurchased === 'true') {
      toast.success('Credits added to your account!');
      refreshUser();
      navigate('/billing', { replace: true });
    } else if (sessionId && status === 'success') {
      pollPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      toast.info('Payment cancelled');
      navigate('/billing', { replace: true });
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.info('Payment is being processed...');
      return;
    }

    try {
      const response = await axios.get(`${API}/subscriptions/checkout/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid') {
        toast.success('Subscription activated!');
        refreshUser();
        navigate('/billing', { replace: true });
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Payment check error:', error);
    }
  };

  const handleUpgrade = async (tierName) => {
    if (user?.is_founder) {
      toast.info('You have founder access - all features are free!');
      return;
    }

    if (tierName === user?.tier && user?.subscription_status === 'subscribed') {
      toast.info('You are already on this plan');
      return;
    }

    setCheckoutLoading(tierName);
    try {
      const response = await axios.post(
        `${API}/subscriptions/checkout`,
        { tier: tierName, billing_cycle: billingCycle },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.success('Plan updated!');
        refreshUser();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Checkout failed');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleManageBilling = async () => {
    try {
      const response = await axios.post(
        `${API}/subscriptions/portal`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.url) {
        window.open(response.data.url, '_blank');
      }
    } catch (error) {
      toast.error('Unable to access billing portal');
    }
  };

  const getPrice = (tierKey) => {
    if (billingCycle === 'annual' && annualPricing[tierKey]) {
      return annualPricing[tierKey].annual;
    }
    return tiers[tierKey]?.price || 0;
  };

  const getMonthlyEquivalent = (tierKey) => {
    if (billingCycle === 'annual' && annualPricing[tierKey]) {
      return annualPricing[tierKey].monthly_equivalent;
    }
    return tiers[tierKey]?.price || 0;
  };

  const getSavings = (tierKey) => {
    if (billingCycle === 'annual' && annualPricing[tierKey]) {
      return annualPricing[tierKey].savings;
    }
    return 0;
  };

  const getRenewalDate = () => {
    if (user?.subscription_end) {
      return new Date(user.subscription_end).toLocaleDateString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric'
      });
    }
    return 'N/A';
  };

  // Feature mappings
  const featureLabels = {
    'watermark': 'Watermarked content',
    'no_watermark': 'No watermark',
    'basic_reels': 'Basic Reels',
    'standard_reels': 'HD Reels',
    'pro_reels': 'Pro Reels + Effects',
    'elite_reels': 'Elite Reels + Priority',
    '3_influencers': '3 AI Influencers',
    '5_influencers': '5 AI Influencers',
    '20_influencers': '20 AI Influencers',
    'unlimited_influencers': 'Unlimited Influencers',
    'unlimited': 'Unlimited Everything',
    'voice_cloning': 'Voice Cloning',
    'api_access': 'API Access',
    'priority_support': 'Priority Support',
    'team_access': 'Team Access',
    'white_label': 'White Label',
    'dedicated_support': 'Dedicated Support',
    'all_features': 'All Features',
    'nsfw_mode': 'NSFW Mode',
    'premium_voice': 'Premium Voice',
    'stem_separation': 'Stem Separation',
    'export_wav': 'WAV Export',
    'priority_rendering': 'Priority Rendering Queue',
    'advanced_automation': 'Advanced Automation',
    'advanced_analytics': 'Enhanced Analytics',
    'priority_publishing': 'Publishing Priority',
    'advanced_export': 'Advanced Export Presets',
    'extended_storage': 'Extended Storage Retention',
    'multi_brand_workspace': 'Multi-Brand Workspace',
    'team_seats': 'Team Seats',
    'early_feature_access': 'Early Feature Access',
    'priority_sla': 'Priority Support SLA',
  };

  const tierList = [
    { key: 'standard', name: 'Standard', popular: false },
    { key: 'pro', name: 'Pro', popular: false },
    { key: 'creator_plus', name: 'Creator+', popular: true },
    { key: 'elite', name: 'Elite', popular: false },
    { key: 'agency', name: 'Agency', popular: false },
  ];

  return (
    <AppShell>
      <div className="p-5 sm:p-7 lg:p-10 max-w-6xl mx-auto">
          
          {/* Header */}
          <div className="mb-10 animate-fadeInUp">
            <p className="text-[10px] text-primary/60 uppercase tracking-[0.2em] font-semibold mb-3">Billing</p>
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
              Billing & Subscription
            </h1>
            <p className="text-zinc-500 text-sm mt-2">
              Manage your plan, credits, and payment settings
            </p>
            {/* Trust Badge */}
            <div className="mt-3">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border text-emerald-400 bg-emerald-500/10 border-emerald-500/20">
                <Shield className="w-3 h-3" />
                Secure billing via Stripe
              </span>
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════════════════════
              SECTION 1: Trial / Current Plan Overview
          ═══════════════════════════════════════════════════════════════════════ */}
          {/* Trial Expired Banner */}
          {!user?.is_founder && user?.subscription_status === 'trial_expired' && (
            <div className="mb-6 p-5 rounded-2xl border border-amber-500/30 bg-amber-500/5 animate-fadeInUp" data-testid="trial-expired-banner">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/15 flex items-center justify-center shrink-0 mt-0.5">
                  <Clock className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-base font-semibold text-amber-400">Your free trial has ended</p>
                  <p className="text-sm text-zinc-400 mt-1">Choose a plan below to continue creating content with Delphi AI.</p>
                </div>
              </div>
            </div>
          )}

          {/* Trial Active Banner */}
          {!user?.is_founder && user?.subscription_status === 'trial_active' && (
            <div className="mb-6 p-5 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 animate-fadeInUp" data-testid="trial-active-banner">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/15 flex items-center justify-center shrink-0 mt-0.5">
                  <Clock className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-base font-semibold text-emerald-400">Free Trial Active</p>
                  <p className="text-sm text-zinc-400 mt-1">
                    {user?.trial_end ? (
                      <>Your trial ends {new Date(user.trial_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}. Choose a plan to continue after your trial.</>
                    ) : (
                      <>Enjoy full access during your 7-day trial. Choose a plan to continue after.</>
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}

          <section className="bg-zinc-900/50 border border-zinc-800/50 rounded-2xl p-6 mb-8 animate-fadeInUp delay-75 card-lift" data-testid="plan-overview">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/20 to-purple-500/20 flex items-center justify-center">
                  {user?.is_founder ? (
                    <Crown className="w-7 h-7 text-amber-400" />
                  ) : (
                    <Sparkles className="w-7 h-7 text-primary" />
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-bold text-white">
                      {user?.is_founder ? 'Founder' : 
                       user?.subscription_status === 'subscribed' ? tierDisplayName(user?.tier) :
                       user?.subscription_status === 'trial_active' ? 'Free Trial' :
                       user?.subscription_status === 'trial_expired' ? 'Trial Ended' :
                       'Getting Started'} Plan
                    </h2>
                  </div>
                  <p className="text-sm text-zinc-500 mt-0.5">
                    {user?.is_founder 
                      ? 'Unlimited access to all features and tiers'
                      : user?.subscription_status === 'trial_active'
                        ? `7-day free trial${user?.trial_end ? ` — ends ${new Date(user.trial_end).toLocaleDateString()}` : ''}`
                        : user?.subscription_status === 'subscribed'
                          ? <>
                              {user?.billing_cycle === 'annual' ? 'Annual billing' : 'Monthly billing'} 
                              {user?.subscription_end && ` • Renews ${getRenewalDate()}`}
                            </>
                          : 'Choose a plan to start creating'
                    }
                  </p>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-3">
                <Button 
                  variant="outline" 
                  className="border-zinc-700 hover:bg-zinc-800"
                  onClick={handleManageBilling}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Manage Billing
                </Button>
              </div>
            </div>
            
            {/* Reassurance text */}
            <p className="text-xs text-zinc-500 mt-4 flex items-center gap-1">
              <Shield className="w-3 h-3" />
              You can cancel anytime. Changes apply at the next billing cycle.
            </p>
          </section>

          {/* ═══════════════════════════════════════════════════════════════════════
              SECTION 2: Credit Dashboard
          ═══════════════════════════════════════════════════════════════════════ */}
          <section className="mb-8 animate-fadeInUp delay-100" data-testid="credits-section">
            {/* Prominent Credit Balance Banner */}
            {creditData && (
              <div className="mb-4 p-4 bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-zinc-900 border border-amber-500/20 rounded-xl flex items-center justify-between" data-testid="billing-credit-banner">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                    <Coins className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm text-zinc-400">Credits Remaining</p>
                    <p className="text-2xl font-black text-amber-400" data-testid="billing-credit-balance">{(creditData.balance || 0).toLocaleString()}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-zinc-500">{creditData.monthly_remaining || 0} monthly · {creditData.purchased_credits || 0} purchased</p>
                </div>
              </div>
            )}
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Coins className="w-5 h-5 text-primary" />
              Your Credits
            </h2>
            <CreditDashboard />
            
            {/* Credit Info */}
            <div className="mt-4 p-4 bg-zinc-900/30 border border-zinc-800/50 rounded-xl">
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-zinc-400">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    <span>Monthly credits reset each billing cycle</span>
                  </div>
                  <div className="flex items-center gap-2 text-zinc-400">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    <span>Purchased credits never expire</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-zinc-400">
                    <Zap className="w-4 h-4 text-primary" />
                    <span>Credits power advanced features</span>
                  </div>
                  <div className="flex items-center gap-2 text-zinc-400">
                    <Zap className="w-4 h-4 text-primary" />
                    <span>Basic generation is always free</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ═══════════════════════════════════════════════════════════════════════
              SECTION 3: Plan Comparison & Upgrade
          ═══════════════════════════════════════════════════════════════════════ */}
          <section className="mb-8 animate-fadeInUp delay-150" data-testid="plans-section">
            {/* Founder Banner — shown once at top, never per card */}
            {user?.is_founder && (
              <div className="mb-6 p-4 rounded-xl border border-amber-500/30 bg-amber-500/5 flex items-center gap-3" data-testid="founder-banner">
                <div className="w-10 h-10 rounded-lg bg-amber-500/15 flex items-center justify-center shrink-0">
                  <Crown className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-amber-400">Founder Account — Unlimited Access Enabled</p>
                  <p className="text-xs text-zinc-500 mt-0.5">All features and tiers are included with your founder plan. No action needed.</p>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <h2 className="text-lg font-semibold text-white">Choose Your Plan</h2>
              
              {/* Billing Cycle Toggle */}
              <div className="flex items-center gap-2 bg-zinc-900/50 border border-zinc-800/50 rounded-full p-1">
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    billingCycle === 'monthly' 
                      ? 'bg-primary text-white' 
                      : 'text-zinc-400 hover:text-white'
                  }`}
                  data-testid="billing-toggle-monthly"
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingCycle('annual')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1 ${
                    billingCycle === 'annual' 
                      ? 'bg-primary text-white' 
                      : 'text-zinc-400 hover:text-white'
                  }`}
                  data-testid="billing-toggle-annual"
                >
                  Annual
                  <span className="px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full ml-1">
                    -17%
                  </span>
                </button>
              </div>
            </div>

            {/* Plans Grid */}
            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="h-80 bg-zinc-800/30 rounded-2xl animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
                {tierList.map((tier) => {
                  const tierData = tiers[tier.key];
                  if (!tierData) return null;
                  
                  const price = getPrice(tier.key);
                  const monthlyEquivalent = getMonthlyEquivalent(tier.key);
                  const savings = getSavings(tier.key);
                  const credits = tierCredits[tier.key] || 0;
                  const features = tierData.features?.map(f => featureLabels[f] || f) || [];

                  return (
                    <PlanCard
                      key={tier.key}
                      name={tier.name}
                      price={price}
                      monthlyEquivalent={monthlyEquivalent}
                      savings={savings}
                      features={features}
                      credits={credits}
                      isCurrentPlan={user?.tier === tier.key}
                      isFounder={user?.is_founder}
                      isPopular={tier.popular}
                      billingCycle={billingCycle}
                      onUpgrade={() => handleUpgrade(tier.key)}
                      loading={checkoutLoading === tier.key}
                    />
                  );
                })}
              </div>
            )}
          </section>

          {/* ═══════════════════════════════════════════════════════════════════════
              SECTION 4: Feature Comparison Table
          ═══════════════════════════════════════════════════════════════════════ */}
          <section className="mb-8 overflow-x-auto" data-testid="features-comparison">
            <h2 className="text-lg font-semibold text-white mb-4">Feature Comparison</h2>
            <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-2xl overflow-hidden">
              <table className="w-full min-w-[650px]">
                <thead>
                  <tr className="border-b border-zinc-800/50 bg-zinc-800/30">
                    <th className="py-4 px-4 text-left text-sm font-medium text-zinc-400">Feature</th>
                    <th className="py-4 px-4 text-center text-sm font-medium text-zinc-400">Standard</th>
                    <th className="py-4 px-4 text-center text-sm font-medium text-zinc-400">Pro</th>
                    <th className="py-4 px-4 text-center text-sm font-medium text-orange-400 bg-orange-500/5">Creator+</th>
                    <th className="py-4 px-4 text-center text-sm font-medium text-zinc-400">Elite</th>
                    <th className="py-4 px-4 text-center text-sm font-medium text-zinc-400">Agency</th>
                  </tr>
                </thead>
                <tbody>
                  <FeatureRow feature="AI Influencers" standard="5" pro="20" creatorPlus="Unlimited" elite="Unlimited" agencyScale="Unlimited" />
                  <FeatureRow feature="Premium Voice" standard={false} pro={true} creatorPlus={true} elite={true} agencyScale={true} />
                  <FeatureRow feature="NSFW Mode" standard={false} pro={true} creatorPlus={true} elite={true} agencyScale={true} />
                  <FeatureRow feature="Music Studio" standard="Full" pro="Full + Stems" creatorPlus="Full + Stems" elite="Full + Stems" agencyScale="Full + Stems" />
                  <FeatureRow feature="Stem Separation" standard={false} pro={true} creatorPlus={true} elite={true} agencyScale={true} />
                  <FeatureRow feature="WAV Export" standard={false} pro={true} creatorPlus={true} elite={true} agencyScale={true} />
                  <FeatureRow feature="Priority Rendering" standard={false} pro={false} creatorPlus={true} elite={true} agencyScale="Dedicated" />
                  <FeatureRow feature="Advanced Automation" standard={false} pro={false} creatorPlus={true} elite={true} agencyScale={true} />
                  <FeatureRow feature="Enhanced Analytics" standard={false} pro={false} creatorPlus={true} elite={true} agencyScale={true} />
                  <FeatureRow feature="Publishing Priority" standard={false} pro={false} creatorPlus={true} elite={true} agencyScale={true} />
                  <FeatureRow feature="Priority Support" standard={false} pro={false} creatorPlus={false} elite={true} agencyScale="SLA" />
                  <FeatureRow feature="Multi-Brand Workspace" standard={false} pro={false} creatorPlus={false} elite={false} agencyScale={true} />
                  <FeatureRow feature="Team Seats" standard={false} pro={false} creatorPlus={false} elite={false} agencyScale={true} />
                  <FeatureRow feature="Early Feature Access" standard={false} pro={false} creatorPlus={false} elite={false} agencyScale={true} />
                  <FeatureRow feature="Unlimited Everything" standard={false} pro={false} creatorPlus={false} elite={true} agencyScale={true} />
                  <FeatureRow feature="Watermark" standard="No" pro="No" creatorPlus="No" elite="No" agencyScale="No" />
                  <FeatureRow feature="Monthly Credits" standard="250" pro="1,000" creatorPlus="2,000" elite="3,000" agencyScale="5,000" />
                </tbody>
              </table>
            </div>
            <p className="text-xs text-zinc-500 mt-3 text-center">
              All plans include basic video, image, and music generation. Start with a 7-day free trial.
            </p>
          </section>

          {/* ═══════════════════════════════════════════════════════════════════════
              SECTION 5: Billing Portal Access
          ═══════════════════════════════════════════════════════════════════════ */}
          <section className="bg-zinc-900/50 border border-zinc-800/50 rounded-2xl p-6 animate-fadeInUp delay-250" data-testid="billing-portal">
            <h2 className="text-lg font-semibold text-white mb-4">Billing Management</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <button
                onClick={handleManageBilling}
                className="flex flex-col items-center p-4 bg-zinc-800/30 rounded-xl hover:bg-zinc-800/50 transition-all group card-lift btn-press"
              >
                <CreditCard className="w-6 h-6 text-zinc-400 group-hover:text-primary mb-2" />
                <span className="text-sm font-medium text-white">Update Payment</span>
                <span className="text-xs text-zinc-500">Change card or method</span>
              </button>
              <button
                onClick={handleManageBilling}
                className="flex flex-col items-center p-4 bg-zinc-800/30 rounded-xl hover:bg-zinc-800/50 transition-all group card-lift btn-press"
              >
                <Download className="w-6 h-6 text-zinc-400 group-hover:text-primary mb-2" />
                <span className="text-sm font-medium text-white">Download Invoices</span>
                <span className="text-xs text-zinc-500">Past billing history</span>
              </button>
              <button
                onClick={handleManageBilling}
                className="flex flex-col items-center p-4 bg-zinc-800/30 rounded-xl hover:bg-zinc-800/50 transition-all group card-lift btn-press"
              >
                <Calendar className="w-6 h-6 text-zinc-400 group-hover:text-primary mb-2" />
                <span className="text-sm font-medium text-white">Billing History</span>
                <span className="text-xs text-zinc-500">View all transactions</span>
              </button>
              <button
                onClick={handleManageBilling}
                className="flex flex-col items-center p-4 bg-zinc-800/30 rounded-xl hover:bg-zinc-800/50 transition-all group card-lift btn-press"
              >
                <XCircle className="w-6 h-6 text-zinc-400 group-hover:text-red-400 mb-2" />
                <span className="text-sm font-medium text-white">Cancel Subscription</span>
                <span className="text-xs text-zinc-500">Downgrade to trial</span>
              </button>
            </div>
            
            <p className="text-xs text-zinc-500 mt-4 flex items-center justify-center gap-1">
              <Shield className="w-3 h-3" />
              Secure billing powered by Stripe. We never store your card details.
            </p>
          </section>

        </div>
    </AppShell>
  );
};

export default BillingPage;
